/* eslint-disable @typescript-eslint/no-unused-vars, no-unused-vars */
import React from 'react';
import { FloatingWrapper } from '../index.js';
import type { DSHookFloatingContextT } from '../index.js';

// test we expose the namespace and the namespace follows our deliverable conventions
type ComponentPropsForApp = DSHookFloatingContextT.Props;
type ComponentPropsInternals = DSHookFloatingContextT.InternalProps;
type ComponentPropsDefaultProps = DSHookFloatingContextT.DefaultProps;
type ComponentPropsOptionalProps = DSHookFloatingContextT.OptionalProps;

const testOptionalProps: ComponentPropsOptionalProps = {};

// difference Props and InternalProps is that InternalProps has all the default props filled in
// Props allows for partial defaults
const testPartialDefaults: Partial<ComponentPropsDefaultProps> = {
  withoutAnimation: false,
  withoutPortal: false,
  animationDuration: 300,
};

// InternalProps requires all defaults to be filled in
const testCompleteDefaults: Required<ComponentPropsDefaultProps> = {
  withoutAnimation: false,
  withoutPortal: false,
  animationDuration: 300,
  placement: 'top',
  customOffset: [10, 10],
  portalDOMContainer: document.body,
};

const testInternalProps: ComponentPropsInternals = {
  ...testOptionalProps,
  ...testCompleteDefaults,
};

const testInternalPropsAsSyntax = {
  ...testOptionalProps,
  ...testCompleteDefaults,
} as ComponentPropsInternals;

// using the explicit type definition, if there is an error, it will be marked on the key that is wrong
const testExplicitDefinition: ComponentPropsForApp = {
  animationDuration: 300,
  withoutAnimation: false,
  withoutPortal: false,
  placement: 'top',
  customOffset: [10, 10],
  portalDOMContainer: document.body,
  ...testOptionalProps,
};

// using the "as" syntax, if there is an error, it will be marking the whole object as wrong because it is not compatible with the type
const testInferedTypeCompatibility = {
  ...testOptionalProps,
  ...testPartialDefaults,
  animationDuration: 300,
} as ComponentPropsForApp;

const testDefinitionAsConst = {
  animationDuration: 300,
  withoutAnimation: false,
  withoutPortal: false,
  placement: 'top',
  portalDOMContainer: document.body,
} as const;

const ExampleUsageComponent = () => (
  <>
    {/* works with explicitly casted props, all syntaxes */}
    <FloatingWrapper
      {...testExplicitDefinition}
      innerRef={React.createRef<HTMLDivElement>()}
      isOpen
      floatingStyles={{}}
    >
      <div>Content</div>
    </FloatingWrapper>
    <FloatingWrapper
      {...testInferedTypeCompatibility}
      innerRef={React.createRef<HTMLDivElement>()}
      isOpen
      floatingStyles={{}}
    >
      <div>Content</div>
    </FloatingWrapper>
    <FloatingWrapper {...testDefinitionAsConst} innerRef={React.createRef<HTMLDivElement>()} isOpen floatingStyles={{}}>
      <div>Content</div>
    </FloatingWrapper>
    {/* works with inline values */}
  </>
);
